// frontend/src/App.tsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectPage } from './pages/ProjectPage';
import { ConversationPage } from './pages/ConversationPage';
import { PublicProjectPage } from './pages/PublicProjectPage';
import { HomePage } from './pages/HomePage';
import { UtilitiesPage } from './pages/UtilitiesPage';
import { AdminPage } from './pages/AdminPage';
import { AdminVideosPage } from './pages/AdminVideosPage';
import { AdminFilesPage } from './pages/AdminFilesPage';
import { AdminAgentPage } from './pages/AdminAgentPage';
import { AdminPromotePage } from './pages/AdminPromotePage';
import { AdminMiniAppsPage } from './pages/AdminMiniAppsPage';
import { AdminFamiliesPage } from './pages/AdminFamiliesPage';
import { AdminBaseToolForkGeneratorPage } from './pages/AdminBaseToolForkGeneratorPage';
import { AdminProblemReportsPage } from './pages/AdminProblemReportsPage';
import { AdminCodeRefreshPage } from './pages/AdminCodeRefreshPage';
import { AdminBackendRebuildsPage } from './pages/AdminBackendRebuildsPage';
import { AdminApiKeysPage } from './pages/AdminApiKeysPage';
import { AdminCreditUsagePage } from './pages/AdminCreditUsagePage';
import { PricingPage } from './pages/PricingPage';
import { EarningsPage } from './pages/EarningsPage';
import { CreatorsPage } from './pages/CreatorsPage';
import { UserGuidePage } from './pages/UserGuidePage';
import { DocumentationPage } from './pages/DocumentationPage';
import { VibeCodingTutorialPage } from './pages/VibeCodingTutorialPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CopyrightPolicyPage } from './pages/CopyrightPolicyPage';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { ProfilePage } from './pages/ProfilePage';
import { StarredPage } from './pages/StarredPage';
import { InfolinksAds } from './components/InfolinksAds';

function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <InfolinksAds />
            <Routes>
              {/* Home page - shows public projects */}
              <Route path="/" element={<HomePage />} />
              <Route path="/utilities" element={<UtilitiesPage />} />

              {/* Pricing page - publicly accessible */}
              <Route path="/pricing" element={<PricingPage />} />

              {/* Creators page - publicly accessible */}
              <Route path="/creators" element={<CreatorsPage />} />

              {/* User Guide page - publicly accessible */}
              <Route path="/guide" element={<UserGuidePage />} />

              {/* Documentation pages - publicly accessible */}
              <Route path="/docs" element={<DocumentationPage />} />
              <Route
                path="/docs/how-to-vibe-code-an-application-with-llambada"
                element={<VibeCodingTutorialPage />}
              />

              {/* Info pages - publicly accessible */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/copyright" element={<CopyrightPolicyPage />} />

              {/* Public routes (redirect if logged in) */}
              <Route element={<PublicOnlyRoute />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              </Route>

              {/* Password reset - accessible with valid token (handled by Supabase) */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected routes (require login) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/starred" element={<StarredPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/earnings" element={<EarningsPage />} />
                <Route path="/project/:projectId" element={<ProjectPage />} />
                <Route
                  path="/project/:projectId/conversation/:conversationId"
                  element={<ConversationPage />}
                />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/videos" element={<AdminVideosPage />} />
                <Route path="/admin/files" element={<AdminFilesPage />} />
                <Route path="/admin/agent" element={<AdminAgentPage />} />
                <Route path="/admin/miniapps" element={<AdminMiniAppsPage />} />
                <Route path="/admin/families" element={<AdminFamiliesPage />} />
                <Route path="/admin/base-tool-forks" element={<AdminBaseToolForkGeneratorPage />} />
                <Route path="/admin/promote" element={<AdminPromotePage />} />
                <Route path="/admin/reports" element={<AdminProblemReportsPage />} />
                <Route path="/admin/code-refresh" element={<AdminCodeRefreshPage />} />
                <Route path="/admin/backend-rebuilds" element={<AdminBackendRebuildsPage />} />
                <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
                <Route path="/admin/credit-usage" element={<AdminCreditUsagePage />} />
              </Route>

              {/* Public project page */}
              <Route path="/p/:publicId/:slug?" element={<PublicProjectPage />} />

              {/* Public user profile page */}
              <Route path="/u/:username" element={<PublicProfilePage />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
